import Badge from '@passkeys/ui/badge';
import SegmentedControl from '@passkeys/ui/segmented-control';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import TokenIcon from '@passkeys/ui/token-icon';
import { useState } from 'react';

import type { Asset } from '@/types';
import {
  formatCrypto,
  formatFiat,
  formatPercentage,
} from '@/ui/utils/formatting';

type HeroProps = {
  asset?: Asset;
  balance?: number;
  price?: number;
  priceChangePercentage?: number;
  priceUnavailable?: boolean;
};

function Hero({
  asset,
  balance,
  price,
  priceChangePercentage,
  priceUnavailable = false,
}: HeroProps) {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const showBalance = activeIndex === 0;

  const isLoading =
    !asset ||
    balance === undefined ||
    price === undefined ||
    priceChangePercentage === undefined;

  return (
    <div className="flex flex-col items-center">
      {isLoading ? (
        <Skeleton height={80} width={80} />
      ) : (
        <TokenIcon
          name={asset.name}
          displayName={asset.displayName}
          size={80}
          chainIconSize={32}
        />
      )}

      <div className="mb-6 mt-8 flex w-full flex-col items-center gap-y-2">
        {isLoading ? (
          <>
            <Skeleton height={24} width={40} />
            <Skeleton height={24} width={80} />
            <Skeleton height={16} width={60} className="my-1" />
          </>
        ) : (
          <>
            <Badge className="font-mono uppercase">
              {asset.baseAsset.displayTicker}
            </Badge>
            <Text size={24} align="center" className="leading-none">
              {showBalance
                ? formatCrypto(balance, asset.displayTicker)
                : formatFiat(priceUnavailable ? null : price)}
            </Text>

            {showBalance ? (
              <Text
                monospace
                className="text-black/40 theme-dark:text-neutral-400"
              >
                {formatFiat(balance * price)}
              </Text>
            ) : (
              <Badge variant={priceChangePercentage >= 0 ? 'green' : 'red'}>
                {formatPercentage(
                  priceUnavailable ? null : priceChangePercentage,
                )}
              </Badge>
            )}
          </>
        )}
      </div>

      <SegmentedControl
        activeIndex={activeIndex}
        segments={[
          {
            label: 'Balance',
            onClick: () => setActiveIndex(0),
          },
          {
            label: 'Price',
            onClick: () => setActiveIndex(1),
          },
        ]}
        disabled={isLoading}
      />
    </div>
  );
}

export default Hero;
